import {
  ChangeEvent,
  MouseEvent,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as CloseIcon } from '$icons/uncolored/Close.svg';
import { ReactComponent as Pencil } from '$icons/uncolored/pencil.svg';
import { ReactComponent as Checkmark } from '$icons/uncolored/checkmark.svg';
import FeedbackType from '$constants/FeedbackType';
import { candidatesApi } from '$store/modules/candidates';
import {
  companiesApi,
  useRejectCandidateFromPositionEmployeeMutation,
} from '$store/modules/companies';
import { segmentTrack } from '$utils/handleSegment';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import SPKToast from '$components/SPKToast/SPKToast';

const SPKEmployerCandidateFeedBackModal = ({
  candidateId,
  setSwitchingId,
  closeHandler,
  position,
}: {
  candidateId: number;
  setSwitchingId: Dispatch<SetStateAction<number | null>>;
  closeHandler: () => void;
  position: number;
}) => {
  const [t] = useTranslation('global');
  const dispatch = useAppDispatch();

  const SPKRejectFeedbacks = useMemo(
    () => [
      {
        label: t('FeedbackReject.SkillsNotAligned'),
        value: FeedbackType.CANDIDATE_NOT_MEET_REQUIREMENTS,
      },
      {
        label: t('FeedbackReject.CommunicationStyle'),
        value: FeedbackType.CANDIDATE_COMMUNICATION_NOT_STRONG_ENOUGH,
      },
      {
        label: t('FeedbackReject.WorkStyleDoesntFit'),
        value: FeedbackType.CANDIDATE_PERSONALITY_DOESNT_MATCH_OUR_NEEDS,
      },
      { label: t('FeedbackReject.OtherReason'), value: FeedbackType.OTHER },
    ],
    [t]
  );

  const [rejectCandidateFromPositionEmployee] =
    useRejectCandidateFromPositionEmployeeMutation();

  const { handleSubmit, watch, setValue, register } = useForm<{
    feedbackType: null | string;
    feedbackText: string;
  }>({
    defaultValues: {
      feedbackType: null,
      feedbackText: '',
    },
  });

  const feedbackRadioValue = watch('feedbackType');
  const feedbackTextValue = watch('feedbackText');

  const feedbackRadioHandler = (
    ev: MouseEvent<HTMLInputElement> | ChangeEvent<HTMLInputElement>
  ) => {
    const target = ev.target as HTMLInputElement;
    if (feedbackRadioValue === target.value) {
      setValue('feedbackType', null);
    } else {
      setValue('feedbackType', target.value);
    }
  };

  const submitFeedbackHandler: SubmitHandler<{
    feedbackType: string | null;
    feedbackText: string;
  }> = ({ feedbackType, feedbackText }) => {
    const feedbackTypeId = parseInt(feedbackType as string, 10);
    rejectCandidateFromPositionEmployee({
      query: {
        candidateId,
        positionId: +position,
        FeedbackType: feedbackTypeId,
        Feedback: feedbackText,
      },
      hideToast: true,
    }).then(() => {
      dispatch(candidatesApi.util.invalidateTags(['Candidates']));
      dispatch(companiesApi.util.invalidateTags(['PositionsList']));

      SPKToast(t('FeedbackReject.SentSuccessfully'));
      segmentTrack('Candidate Rejected', {
        candidate_id: candidateId,
        reject_type: feedbackTypeId,
        reject_reason: feedbackText,
      });
      setSwitchingId(candidateId);
      closeHandler();
    });
  };

  return (
    <div className="bg-white p-8 rounded-xl font-mulish md:min-w-[600px]">
      <button
        type="button"
        className="text-spk-mediumContrast text-sm items-center flex flex-row justify-end ml-auto"
        onClick={() => closeHandler()}
      >
        Close
        <CloseIcon className="stroke-spk-mediumContrast w-4 h-4" />
      </button>
      <h1 className="text-2xl font-extrabold mb-2">
        {t('FeedbackReject.Title')}
      </h1>
      <h3 className="text-spk-mediumContrast text-sm mb-4">
        <p>{t('FeedbackReject.SPKSubtitle1')}</p>
        <p>{t('FeedbackReject.SPKSubtitle2')}</p>
      </h3>
      <form onSubmit={handleSubmit(submitFeedbackHandler)}>
        <div className="flex flex-col gap-4 mb-4">
          {SPKRejectFeedbacks.map((singleFeedbackReason) => (
            <label
              key={`reject_candidate_feedback_${candidateId}_${singleFeedbackReason.value}`}
              className={`rounded-md border border-spk-neutral-40 p-2 gap-2 flex items-center select-none ${
                feedbackRadioValue === `${singleFeedbackReason.value}` &&
                'bg-spk-neutral-10'
              }`}
            >
              <div className="relative flex flex-row items-center justify-center select-none">
                <input
                  type="radio"
                  {...register('feedbackType')}
                  value={singleFeedbackReason.value}
                  onClick={feedbackRadioHandler}
                  onChange={feedbackRadioHandler}
                  checked={
                    feedbackRadioValue === `${singleFeedbackReason.value}`
                  }
                  className="peer appearance-none h-5 w-5 border-2 border-spk-neutral-40 rounded-md checked:border-spk-brightBlue checked:bg-spk-brightBlue transition-colors"
                />
                <span className="absolute text-white opacity-0 peer-checked:opacity-100 pointer-events-none">
                  <Checkmark className="fill-spk-softBlue" />
                </span>
              </div>
              {singleFeedbackReason.label}
            </label>
          ))}
        </div>
        <p className="text-spk-mediumContrast text-xs mb-2">
          {t('FeedbackReject.AdditionalFeedback')}
        </p>
        <div className="mb-8 relative">
          <textarea
            className="outline-none min-h-24 w-full border border-spk-neutral-40 rounded-md flex justify-start overflow-hidden p-2"
            {...register('feedbackText', {
              onChange: () => {
                if (!feedbackRadioValue)
                  setValue(
                    'feedbackType',
                    SPKRejectFeedbacks[SPKRejectFeedbacks.length - 1].label
                  );
              },
            })}
          />
          {feedbackTextValue.length === 0 && (
            <Pencil className="fill-spk-mediumContrast absolute top-3 left-3" />
          )}
        </div>
        <button
          type="submit"
          className="flex ml-auto text-spk-softBlue font-bold text-sm bg-spk-brightBlue rounded-full px-8 py-2 disabled:opacity-25 disabled:cursor-not-allowed"
          disabled={!feedbackRadioValue}
        >
          Send Feedback
        </button>
      </form>
    </div>
  );
};

export default SPKEmployerCandidateFeedBackModal;
